// A class as its student reads it, and as its instructor previews a
// student's view (byj-1wv.51.6, byj-1wv.51.20): the class page draws it for
// a student and the preview for its instructor, so neither imports the other.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type AppTemplate, type ClassDetail, type ExamAssignmentView, type Recording, type ShareFile, type ShareSummary } from "../api";
import { NoAssignment, sessionLabel } from "../classroom";
import { windowState } from "../exams";
import { useChooseAssignment } from "./ChooseAssignment";
import { Link } from "./Nav";
import { Page, PageHead } from "./Page";
import { RecordingRow } from "./RecordingRow";
import type { MenuItem } from "./RowMenu";
import { EmptyRows, Grouped, liveThenEarlier, type Group } from "./Rows";
import { ShareRow } from "./ShareRow";
import { panelId, tabId, Tabs } from "./Tabs";
import { liveThenNewest } from "../kinds";
import { useKeptChoice } from "../kept";
import { useNow } from "../now";
import { t, when, whenMinute, whenShort, word } from "../i18n";
import { AssignmentCards, PolicyBar } from "./StudentWork";
import { fileBytes, putNewFile } from "../files";
import { createCommand, TemplateRow, templateItems } from "./TemplateParts";
import { AuthorCommands } from "./CommandLine";
import { Announcements } from "./Announcements";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

const noShares = async (): Promise<ShareSummary[]> => [];
const noRecordings = async (): Promise<Recording[]> => [];

type StudentTab = "assignments" | "exams" | "shares" | "recordings" | "files" | "templates";
const studentTabs: StudentTab[] = ["assignments", "exams", "shares", "recordings", "files", "templates"];

/**
 * The exams assigned to this class, for a student: the ones /exams lists as
 * assigned to them, of this class (byj-1wv.51.12). In the instructor's
 * preview, the class's own, which only its instructor may read.
 */
const studentExams = (classId: string) => async (): Promise<ExamAssignmentView[]> => (await api.exams()).assigned.filter((a) => a.class.id === classId);
const previewExams = (classId: string) => (): Promise<ExamAssignmentView[]> => api.classExams(classId);

/** An exam as a row of the student's Exams tab: its window, opened on its page — in the preview, not opened. */
function ExamItem({ a, open, now }: { a: ExamAssignmentView; open: boolean; now: number }) {
  const state = windowState(a, now);
  return (
    <li className="share-row rec-row is-live no-menu">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        exm
      </span>
      <div className="row-main">
        {open ? (
          <Link to={`/ea/${encodeURIComponent(a.id)}`} className="row-title">
            {a.exam.title}
          </Link>
        ) : (
          <span className="row-title">{a.exam.title}</span>
        )}
        <div className="row-sub">
          {t("exams:examVersion", { v: a.version })}
          {a.opens_at && <> · {t("exams:opensAt", { w: whenMinute(a.opens_at) })}</>}
          {a.closes_at && <> · {t("exams:closesAt", { w: whenMinute(a.closes_at) })}</>}
        </div>
      </div>
      <div className="row-meta">
        <span className={`chip ${state === "open" ? "chip-live" : ""}`}>{word("exams:examWindowState", state)}</span>
      </div>
    </li>
  );
}

/** A file as a row of the student's Files tab: opened on its page. */
function FileItem({ f, sub }: { f: ShareFile; sub?: string }) {
  return (
    <li className="share-row rec-row is-ended no-menu">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        {f.kind === "folder" ? "dir" : "fil"}
      </span>
      <div className="row-main">
        <Link to={`/f/${encodeURIComponent(f.id)}`} className="row-title">
          {f.name}
        </Link>
        <div className="row-sub">
          {sub ? `${sub} · ` : ""}
          {f.latest ? `${t("files:fileVersion", { n: f.latest })} · ${fileBytes(f.bytes)}` : t("files:fileNoVersion")}
        </div>
      </div>
      <div className="row-meta">
        <time className="row-time" dateTime={f.updated_at} title={when(f.updated_at)}>
          {whenShort(f.updated_at)}
        </time>
      </div>
    </li>
  );
}

/**
 * The student's page (byj-1wv.51.6): what the instructor may do as a bar
 * under the head, then tabs — Assignments, each open one a card with its
 * share command and how far they have got; the exams assigned to the class,
 * each to its page (byj-1wv.51.12); their shares to the class as
 * /shares' rows; their recordings made in it under each assignment — the tab
 * kept per browser. `preview`, it is the instructor's look at it: their own
 * shares are not a student's, so none are listed — but `example`'s, made up
 * to show how a student's look, marked so — and no row acts.
 */
export function StudentClass({
  cls,
  shares: load = api.shares,
  recordings: loadRecs = api.recordings,
  files: loadFiles = api.files,
  classFiles: loadClassFiles = api.classFiles,
  classTemplates: loadClassTemplates = api.classAppTemplates,
  handIn: putHandIn = (f: File, under: string) => putNewFile(api, f, under).then(() => undefined),
  exams: loadExams,
  preview = false,
  example,
}: {
  cls: ClassDetail;
  shares?: () => Promise<ShareSummary[]>;
  recordings?: () => Promise<Recording[]>;
  /** my files, of which my hand-ins in this class (RFC-054 §5) */
  files?: () => Promise<{ mine: ShareFile[] }>;
  /** what the instructor gave the class */
  classFiles?: (id: string) => Promise<ShareFile[]>;
  /** the app templates given to the class (RFC-052 §5, byj-1wv.77) */
  classTemplates?: (id: string) => Promise<AppTemplate[]>;
  handIn?: (f: File, assignment: string) => Promise<void>;
  /** the exams assigned to the class (RFC-048); by default a student's, or the class's own in the preview */
  exams?: () => Promise<ExamAssignmentView[]>;
  preview?: boolean;
  /** In the preview, shares made up to show the list filled; kept stable by the caller. */
  example?: ShareSummary[];
}) {
  const [mine, setMine] = useState<ShareSummary[] | null>(null);
  // their recordings: none known until read, and a failure said where they are listed
  const [recs, setRecs] = useState<Recording[] | null>(null);
  const [recError, setRecError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choose, chooseDialog] = useChooseAssignment();
  // the instructor's preview keeps its own, so their look does not move the student's
  const [tab, setTab] = useKeptChoice<StudentTab>(preview ? "class-preview.student-tab" : "class-student.tab", "assignments", studentTabs);
  // how a due date stands, said again while the page is open (now.ts)
  const now = useNow();
  const fromShares = useMemo(() => (preview ? (example ? async () => example : noShares) : load), [preview, example, load]);
  const fromRecordings = preview ? noRecordings : loadRecs;
  const readRecordings = useCallback(() => {
    setRecError(null);
    // their own made in this class, not another's: the relay says which class each was made in
    fromRecordings().then(
      (all) => setRecs(all.filter((r) => r.class?.id === cls.id && r.role === "owner")),
      (err: unknown) => setRecError(errText(err)),
    );
  }, [cls.id, fromRecordings]);
  // my hand-ins in this class, and what its instructor gave it: none in the preview
  const [handIns, setHandIns] = useState<ShareFile[] | null>(null);
  const [material, setMaterial] = useState<ShareFile[] | null>(null);
  // said in the Files tab, where they are listed, not over the whole page
  const [filesError, setFilesError] = useState<string | null>(null);
  const readFiles = useCallback(() => {
    if (preview) {
      setHandIns([]);
      setMaterial([]);
      return;
    }
    setFilesError(null);
    loadFiles().then((r) => setHandIns(r.mine.filter((f) => f.hand_in?.class === cls.id)), (err: unknown) => setFilesError(errText(err)));
    loadClassFiles(cls.id).then(setMaterial, (err: unknown) => setFilesError(errText(err)));
  }, [cls.id, preview, loadFiles, loadClassFiles]);
  // the app templates given to the class, a failure said in their tab; the preview shows the
  // class's own, the instructor being one who may list them, as a student sees them: none
  // the viewer's own, so each by its owner and its line by its id (createCommand)
  const [templates, setTemplates] = useState<AppTemplate[] | null>(null);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const readTemplates = useCallback(() => {
    setTemplatesError(null);
    loadClassTemplates(cls.id).then(
      (ts) => setTemplates(preview ? ts.map((x) => ({ ...x, mine: false })) : ts),
      (err: unknown) => setTemplatesError(errText(err)),
    );
  }, [cls.id, preview, loadClassTemplates]);
  // the exams assigned to the class, a failure said in their tab
  const [exams, setExams] = useState<ExamAssignmentView[] | null>(null);
  const [examsError, setExamsError] = useState<string | null>(null);
  const fromExams = useMemo(() => loadExams ?? (preview ? previewExams(cls.id) : studentExams(cls.id)), [loadExams, preview, cls.id]);
  const readExams = useCallback(() => {
    setExamsError(null);
    fromExams().then(setExams, (err: unknown) => setExamsError(errText(err)));
  }, [fromExams]);
  useEffect(() => {
    fromShares().then((all) => setMine(all.filter((s) => s.class_id === cls.id && s.role === "owner")), (err: unknown) => setError(errText(err)));
    readRecordings();
    readFiles();
    readTemplates();
    readExams();
  }, [cls.id, fromShares, readRecordings, readFiles, readTemplates, readExams]);
  const handIn = async (f: File, under: string) => {
    await putHandIn(f, under);
    readFiles();
  };
  const assignments = cls.assignments ?? [];
  const open = assignments.filter((a) => !a.archived_at);
  const code = cls.join_code ?? "CODE";
  const change = async (s: ShareSummary) => {
    const title = sessionLabel(s.title, s.id, s.started_at, new Date(now)).text;
    const key = await choose(t("classes:assignmentOf", { name: title }), s.assignment?.key ?? "", assignments);
    if (key === undefined) return;
    try {
      const v = await api.setShareAssignment(s.id, key);
      setMine((list) => (list ?? []).map((x) => (x.id === s.id ? { ...x, assignment: v.assignment } : x)));
    } catch (err) {
      setError(errText(err));
    }
  };
  const menu = (s: ShareSummary): MenuItem[] =>
    !preview && (open.length > 0 || s.assignment) ? [{ label: t("classes:setAssignment"), onSelect: () => void change(s) }] : [];
  const shown = (mine ?? []).slice().sort(liveThenNewest);
  // their recordings under each assignment, as the class lists them, then those of none
  const recGroups: Group<Recording>[] = [
    ...assignments.map((a) => ({ key: a.key, title: a.title, items: (recs ?? []).filter((r) => r.assignment?.key === a.key) })),
    { key: NoAssignment, title: t("classes:assignmentFilterNone"), items: (recs ?? []).filter((r) => !r.assignment) },
  ];
  const prefix = preview ? "preview-student" : "student";
  const live = shown.filter((s) => s.live).length;
  const head: Record<StudentTab, { title: string; count: number | null }> = {
    assignments: { title: t("classes:assignments"), count: open.length },
    exams: { title: t("exams:classExamsStudent"), count: exams === null ? null : exams.length },
    shares: { title: t("classes:myShares"), count: mine === null ? null : shown.length },
    recordings: { title: t("classes:myRecordings"), count: recs === null ? null : recs.length },
    files: { title: t("files:classFilesTab"), count: material === null || handIns === null ? null : material.length + handIns.length },
    templates: { title: t("classes:classTemplatesTab"), count: templates === null ? null : templates.length },
  };
  // the templates with a version to make an app from, each to be shown with its line
  const published = (templates ?? []).filter((x) => x.latest > 0);
  const exampleChip = preview && example && (
    <>
      {" "}
      <span className="chip example-chip">{t("classes:exampleChip")}</span>
    </>
  );
  return (
    // in the preview it is a part of the instructor's page, not a page
    <Page as={preview ? "div" : "main"}>
      {chooseDialog}
      <PageHead
        title={cls.name}
        actions={
          !preview && (
            <Link to="/classes" className="button">
              {t("classes:classesTitle")}
            </Link>
          )
        }
      />
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      <PolicyBar policy={cls.policy} consent={cls.consent} />
      <Announcements items={cls.announcements ?? []} now={now} />
      <Tabs
        prefix={prefix}
        label={t("classes:studentTabs")}
        value={tab}
        onChange={setTab}
        tabs={studentTabs.map((id) => ({
          id,
          label: (
            <>
              {head[id].title}
              {head[id].count !== null && <span className="tab-count">{head[id].count}</span>}
              {id === "shares" && live > 0 && <span className="tab-live">{t("classes:liveCount", { n: live })}</span>}
              {id === "shares" && exampleChip}
            </>
          ),
        }))}
      />
      <div role="tabpanel" id={panelId(prefix, "assignments")} aria-labelledby={tabId(prefix, "assignments")} hidden={tab !== "assignments"}>
        <AssignmentCards code={code} assignments={open} shares={shown} recordings={recs ?? []} now={now} files={handIns ?? []} handIn={preview ? undefined : handIn} />
      </div>
      <div role="tabpanel" id={panelId(prefix, "exams")} aria-labelledby={tabId(prefix, "exams")} hidden={tab !== "exams"} className="rows-panel" data-testid="my-exams">
        {examsError ? (
          <p className="error">
            {t("exams:studentExamsError", { e: examsError })}{" "}
            <button className="small" onClick={readExams}>
              {t("common:retry")}
            </button>
          </p>
        ) : exams === null ? (
          <p className="muted">{t("common:loading")}</p>
        ) : exams.length === 0 ? (
          <EmptyRows text={t("exams:noStudentExams")} />
        ) : (
          <ul className="rows" data-testid="class-student-exams">
            {exams.map((a) => (
              <ExamItem key={a.id} a={a} open={!preview} now={now} />
            ))}
          </ul>
        )}
      </div>
      <div role="tabpanel" id={panelId(prefix, "shares")} aria-labelledby={tabId(prefix, "shares")} hidden={tab !== "shares"} className="rows-panel" data-testid="my-shares">
        {mine === null ? (
          <p className="muted">{t("common:loading")}</p>
        ) : shown.length === 0 ? (
          <EmptyRows text={t("classes:noSharesYet")} />
        ) : (
          <Grouped id="my-shares" groups={liveThenEarlier(shown)} row={(s) => <ShareRow key={s.id} share={s} menu={menu(s)} />} />
        )}
      </div>
      <div role="tabpanel" id={panelId(prefix, "recordings")} aria-labelledby={tabId(prefix, "recordings")} hidden={tab !== "recordings"} className="rows-panel" data-testid="my-recordings">
        {recError ? (
          <p className="error">
            {t("common:error", { m: recError })}{" "}
            <button className="small" onClick={readRecordings}>
              {t("common:retry")}
            </button>
          </p>
        ) : recs === null ? (
          <p className="muted">{t("common:loading")}</p>
        ) : recs.length === 0 ? (
          <EmptyRows text={t("classes:noRecordingsInClass")} />
        ) : (
          <Grouped id="my-recordings" headed groups={recGroups} row={(r) => <RecordingRow key={r.id} r={r} menu={[]} review={false} />} />
        )}
      </div>
      <div role="tabpanel" id={panelId(prefix, "files")} aria-labelledby={tabId(prefix, "files")} hidden={tab !== "files"} className="rows-panel" data-testid="my-files">
        {filesError && (
          <p className="error">
            {t("common:error", { m: filesError })}{" "}
            <button className="small" onClick={readFiles}>
              {t("common:retry")}
            </button>
          </p>
        )}
        <section aria-labelledby={`${prefix}-material`}>
          <h3 id={`${prefix}-material`}>{t("files:classMaterial")}</h3>
          {material === null ? (
            <p className="muted">{t("common:loading")}</p>
          ) : material.length === 0 ? (
            <EmptyRows text={t("files:noClassMaterial")} />
          ) : (
            <ul className="rows" data-testid="class-material">
              {material.map((f) => (
                <FileItem key={f.id} f={f} />
              ))}
            </ul>
          )}
        </section>
        <section aria-labelledby={`${prefix}-handins`}>
          <h3 id={`${prefix}-handins`}>{t("files:myHandIns")}</h3>
          {handIns === null ? (
            <p className="muted">{t("common:loading")}</p>
          ) : handIns.length === 0 ? (
            <EmptyRows text={t("files:noHandIns")} />
          ) : (
            <ul className="rows" data-testid="my-hand-ins">
              {handIns.map((f) => (
                <FileItem key={f.id} f={f} sub={f.hand_in?.title} />
              ))}
            </ul>
          )}
        </section>
      </div>
      <div role="tabpanel" id={panelId(prefix, "templates")} aria-labelledby={tabId(prefix, "templates")} hidden={tab !== "templates"} className="rows-panel" data-testid="class-student-templates">
        {templatesError ? (
          <p className="error">
            {t("common:error", { m: templatesError })}{" "}
            <button className="small" onClick={readTemplates}>
              {t("common:retry")}
            </button>
          </p>
        ) : templates === null ? (
          <p className="muted">{t("common:loading")}</p>
        ) : templates.length === 0 ? (
          <EmptyRows text={t("classes:noStudentTemplates")} />
        ) : (
          <>
            <ul className="rows" data-testid="class-templates">
              {/* in the preview no row acts, as its shares' do not */}
              {templates.map((x) => (
                <TemplateRow key={x.id} x={x} menu={preview ? [] : templateItems(x)} />
              ))}
            </ul>
            {published.length > 0 && (
              <section className="stack" data-testid="class-template-make">
                <p className="muted small flush">{t("classes:classTemplatesMake")}</p>
                {/* each line under its template's name, as the template's own page gives it */}
                {published.map((x) => (
                  <div key={x.id} data-testid={`class-template-make-${x.id}`}>
                    <p className="small flush">{x.title || x.name}</p>
                    <AuthorCommands commands={[createCommand(x)]} />
                  </div>
                ))}
              </section>
            )}
          </>
        )}
      </div>
    </Page>
  );
}
